import apiClient from '../client';

// The work schedule: who works which days, against the volume expected.
// The server does the arithmetic; this file is the shapes and the calls.
//
// NOTE: the shared apiClient's response interceptor returns response.data,
// so every call here resolves to the response BODY.

export const SCHEDULE_DAYS = ['sat', 'sun', 'mon', 'tue', 'wed', 'thu', 'fri'] as const;
export type ScheduleDay = typeof SCHEDULE_DAYS[number];
export const DAY_LABEL: Record<ScheduleDay, string> = {
  sat: 'Sat', sun: 'Sun', mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri',
};

export interface ScheduleDriver {
  accountId: string;
  /** Today's name, from the account. */
  name: string;
  /** The schedule is every current Driver and BC. */
  role?: 'Driver' | 'BC';
  /** False for someone new: nothing saved for them yet. */
  saved?: boolean;
  /** In training: on the roster, but carrying nothing until it is finished. */
  training?: boolean;
  workArea?: string;
  maxStops: number;
  maxPackages: number;
  days: ScheduleDay[];
}

/**
 * A work area (FedEx's WA#, the route) and who runs it: the usual driver
 * every day it is dispatched, except the days in dayDrivers (null there:
 * nobody yet, a floater covers).
 */
export interface ScheduleWorkArea {
  number: string;
  /** FedEx's name for it, "TOPO G05". */
  name?: string;
  /** The days FedEx dispatches it. */
  days: ScheduleDay[];
  driverId?: string | null;
  dayDrivers?: { day: ScheduleDay; accountId: string | null }[];
  note?: string;
}

export interface WorkSchedule {
  drivers: ScheduleDriver[];
  workAreas: ScheduleWorkArea[];
  growthPercent: number;
  /** What a new driver counts at until their own numbers are set; null works it out. */
  defaultMaxStops?: number | null;
  defaultMaxPackages?: number | null;
  /** Read only: what a new driver counts at right now. */
  defaults?: { maxStops: number; maxPackages: number; chosen: boolean };
}

/** Who runs a work area on a day: that day's exception, else the usual driver. */
export const driverOn = (w: ScheduleWorkArea, day: ScheduleDay): string | null => {
  const exception = (w.dayDrivers || []).find((x) => x.day === day);
  return exception ? exception.accountId : (w.driverId || null);
};

export interface WorksheetPerson {
  name: string;
  key: string;
  accountId: string | null;
  personName: string | null;
  deliveryStops: number;
  deliveryPackages: number;
  pickupStops: number;
  pickupPackages: number;
  onRoadHours: number;
  onDutyHours: number;
}

export interface WorksheetName {
  key: string;
  /** As the worksheet spells it: "WILSON,RAUEL ASHER". */
  name: string;
  accountId: string | null;
  personName: string | null;
  /** How it was placed: somebody said so, or the name matched by itself. */
  how: 'alias' | 'name' | null;
  candidates: { accountId: string; name: string | null }[];
}

export interface Worksheet {
  id: string;
  weekStart: string;
  weekEnd: string;
  facility: string;
  serviceArea: string;
  fileName: string;
  uploadedAt: string;
  warnings: string[];
  days: {
    date: string;
    workArea: string;
    workAreaName: string;
    vehicles: string[];
    deliveryStops: number;
    deliveryPackages: number;
    pickupStops: number;
    pickupPackages: number;
    people: WorksheetPerson[];
  }[];
  names: WorksheetName[];
}

export interface SuggestedDriver extends ScheduleDriver {
  fromStatements: boolean;
}

export interface ScheduleOverview {
  plan: WorkSchedule | null;
  suggestions: {
    drivers: SuggestedDriver[];
    workAreas: ScheduleWorkArea[];
    /** Where the suggested work areas came from. */
    workAreasFrom?: 'worksheet' | 'routes';
  };
  /** The newest worksheet uploaded, if any. */
  worksheet?: { weekStart: string; weekEnd: string; unmatched: number } | null;
  defaultGrowthPercent: number;
}

/** Delivery + e-commerce, the sheet's daily totals; pickups beside them. */
export interface DayVolume {
  stops: number;
  packages: number;
  pickupStops: number;
  pickupPackages: number;
}

export interface WeekDay {
  day: ScheduleDay;
  date: string;
  closed: boolean;
  scheduled: number;
  planned: number;
  spare: number;
  maxStops: number;
  maxPackages: number;
  volume: DayVolume | null;
}

export interface WeekDriver extends ScheduleDriver {
  changed: boolean;
  /** What the statement says they did, in a week that has one. */
  driven: Partial<Record<ScheduleDay, { stops: number; packages: number }>>;
}

export type WeekStatus = 'actual' | 'forecast' | 'none';
/**
 * What an expected week was worked out from: last year's days, last
 * year's weekly total spread over the days, or (with no last year) the
 * latest actuals. Each is raised by the growth percent.
 */
export type ForecastBasis = 'last_year_days' | 'last_year_week' | 'recent';

export interface ScheduleWeek {
  weekEnding: string;
  /** False until the company has set up its usual week. */
  plan: boolean;
  status: WeekStatus;
  growthPercent: number;
  basis: ForecastBasis | null;
  weekGrowthPercent: number | null;
  closedDays: ScheduleDay[];
  workAreas: ScheduleWorkArea[];
  drivers: WeekDriver[];
  days: WeekDay[];
}

export interface TimelineDay extends DayVolume {
  day: ScheduleDay;
  date: string;
  maxStops: number | null;
  maxPackages: number | null;
}

export interface TimelineWeek {
  weekEnding: string;
  status: WeekStatus;
  basis: ForecastBasis | null;
  /** The growth percent an expected week was raised by. */
  growthPercent: number | null;
  stops: number | null;
  packages: number | null;
  pickupStops: number | null;
  pickupPackages: number | null;
  maxStops: number | null;
  maxPackages: number | null;
  lastYear: DayVolume | null;
  days: TimelineDay[];
}

export interface WeekChanges {
  drivers: { accountId: string; days: ScheduleDay[] }[];
  closedDays: ScheduleDay[];
  growthPercent: number | null;
}

class ScheduleService {
  private static readonly BASE = '/api/schedule';

  static async overview(): Promise<ScheduleOverview> {
    return apiClient.get(this.BASE) as any;
  }

  static async savePlan(plan: WorkSchedule): Promise<{ plan: WorkSchedule }> {
    return apiClient.put(this.BASE, plan) as any;
  }

  static async week(weekEnding: string): Promise<ScheduleWeek> {
    return apiClient.get(`${this.BASE}/weeks/${weekEnding}`) as any;
  }

  static async saveWeek(weekEnding: string, changes: WeekChanges): Promise<ScheduleWeek> {
    return apiClient.put(`${this.BASE}/weeks/${weekEnding}`, changes) as any;
  }

  static async timeline(): Promise<{ weeks: TimelineWeek[]; thisWeek: string }> {
    return apiClient.get(`${this.BASE}/timeline`) as any;
  }

  static async uploadWorksheet(text: string, fileName: string): Promise<Worksheet> {
    return apiClient.post(`${this.BASE}/worksheets`, { text, fileName }) as any;
  }

  static async worksheet(weekEnd: string): Promise<Worksheet> {
    return apiClient.get(`${this.BASE}/worksheets/${weekEnd.slice(0, 10)}`) as any;
  }

  static async worksheets(): Promise<{ worksheets: { weekStart: string; weekEnd: string; fileName: string; uploadedAt: string; unmatched: number }[] }> {
    return apiClient.get(`${this.BASE}/worksheets`) as any;
  }

  /** Say who a worksheet name is (null forgets it), for every week. */
  static async matchWorksheetName(key: string, accountId: string | null, weekEnd?: string): Promise<Worksheet | null> {
    return apiClient.put(`${this.BASE}/worksheet-names/${encodeURIComponent(key)}`, { accountId, weekEnd: weekEnd?.slice(0, 10) }) as any;
  }
}

export default ScheduleService;
